import { useCallback, useMemo, useRef } from 'react';

import useUpdateOnboardingStep from 'services/bookkeeping/mutations/useUpdateOnboardingStep';
import type { OnboardingProgress } from 'services/bookkeeping/queries/useAccountingOnboardingProgress';

type UploadData = NonNullable<OnboardingProgress['other_document_upload_data']>;
type UploadedDocumentMetadata = UploadData['uploaded_documents'][number];

interface DocumentMetadata {
    uploaded_documents: UploadedDocumentMetadata[];
    document_types: string[];
}

/**
 * The step's `uploaded_documents` / `document_types`, kept as one running copy and written in order.
 *
 * Each write sends the whole list, so building it from the last fetched progress loses whatever an
 * earlier write added before the refetch landed. Every change applies to the running copy instead,
 * writes are queued one after another, and `settled` hands completion the copy once they are done.
 */
export default function useDocumentMetadataWrites(persisted: UploadData | undefined) {
    const updateStep = useUpdateOnboardingStep();
    // Null until the first local change; until then the fetched progress is the copy.
    const localRef = useRef<DocumentMetadata | null>(null);
    const queueRef = useRef<Promise<void>>(Promise.resolve());

    const current = useCallback(
        (): DocumentMetadata =>
            localRef.current ?? {
                uploaded_documents: persisted?.uploaded_documents ?? [],
                document_types: persisted?.document_types ?? [],
            },
        [persisted?.uploaded_documents, persisted?.document_types]
    );

    const write = useCallback(
        (next: DocumentMetadata, onError: (error: unknown) => void) => {
            localRef.current = next;
            // A failed write leaves the copy ahead of the server; the next write carries it all.
            queueRef.current = queueRef.current.then(() =>
                updateStep
                    .mutateAsync({
                        step: 'other_document_upload',
                        status: 'in_progress',
                        data: { ...next, document_count: next.uploaded_documents.length },
                    })
                    .then(() => undefined, onError)
            );
        },
        [updateStep]
    );

    const addDocuments = useCallback(
        (documents: UploadedDocumentMetadata[], documentType: string, onError: (error: unknown) => void) => {
            const base = current();
            write(
                {
                    uploaded_documents: [...base.uploaded_documents, ...documents],
                    document_types: Array.from(new Set([...base.document_types, documentType])),
                },
                onError
            );
        },
        [current, write]
    );

    const removeDocument = useCallback(
        (memberDocumentId: string, onError: (error: unknown) => void) => {
            const base = current();
            write(
                {
                    ...base,
                    uploaded_documents: base.uploaded_documents.filter(
                        (doc) => doc.member_document_id !== memberDocumentId
                    ),
                },
                onError
            );
        },
        [current, write]
    );

    /** Resolves once every queued write has finished, with the copy they wrote. */
    const settled = useCallback(async (): Promise<DocumentMetadata> => {
        await queueRef.current;
        return current();
    }, [current]);

    return useMemo(() => ({ addDocuments, removeDocument, settled }), [addDocuments, removeDocument, settled]);
}
